import {
  FundingPolicyType,
  checkPolicyCalculationTypeEquals,
  roundDownToNearestHundredth,
} from '@shared';
import { isNullish } from '.';

function isFieldAndParsedValueDefined(
  policyField:
    | (PolicyExtractedField & {
        parsedFieldValue: number | undefined;
      })
    | undefined,
): policyField is PolicyExtractedField & { parsedFieldValue: number } {
  return (
    policyField !== undefined && policyField.parsedFieldValue !== undefined
  );
}

function imputeMissingMonthlyMaxValues(
  parsedResponse: ParsedPolicyDataExtractResponse,
): ParsedPolicyDataExtractResponse {
  const {
    monthlyHomeBenefitAmount,
    monthlyAssistedLivingBenefitAmount,
    monthlyIndependentLivingBenefitAmount,
    monthlyNursingHomeBenefitAmount,
  } = parsedResponse;

  const firstNonZeroFacilityMonthlyBenefit =
    monthlyAssistedLivingBenefitAmount?.parsedFieldValue ||
    monthlyIndependentLivingBenefitAmount?.parsedFieldValue ||
    monthlyNursingHomeBenefitAmount?.parsedFieldValue;

  // impute home care monthly max if it is missing with facility monthly max
  if (isFieldAndParsedValueDefined(monthlyHomeBenefitAmount)) {
    if (
      firstNonZeroFacilityMonthlyBenefit &&
      !monthlyHomeBenefitAmount.parsedFieldValue
    ) {
      monthlyHomeBenefitAmount.fieldValue =
        firstNonZeroFacilityMonthlyBenefit.toString();
      monthlyHomeBenefitAmount.parsedFieldValue =
        firstNonZeroFacilityMonthlyBenefit;
      monthlyHomeBenefitAmount.locationDescription =
        'Imputed from first non-zero facility monthly benefit';
    }
  }

  const imputeBenefitAmount = (
    targetField: (PolicyExtractedField & { parsedFieldValue: any }) | undefined,
    fillValue: number | undefined,
    fallbackValue: number | undefined,
    description: string,
    fallBackDescription: string,
  ) => {
    if (targetField) {
      if (fillValue) {
        targetField.fieldValue = fillValue.toString();
        targetField.parsedFieldValue = fillValue;
        targetField.locationDescription = description;
      } else if (fallbackValue) {
        targetField.fieldValue = fallbackValue.toString();
        targetField.parsedFieldValue = fallbackValue;
        targetField.locationDescription = fallBackDescription;
      }
    }
  };

  imputeBenefitAmount(
    monthlyAssistedLivingBenefitAmount,
    firstNonZeroFacilityMonthlyBenefit,
    monthlyHomeBenefitAmount?.parsedFieldValue,
    'Imputed from other facility monthly max',
    'Imputed from Home Care monthly max',
  );

  imputeBenefitAmount(
    monthlyIndependentLivingBenefitAmount,
    firstNonZeroFacilityMonthlyBenefit,
    monthlyHomeBenefitAmount?.parsedFieldValue,
    'Imputed from other facility monthly max',
    'Imputed from Home Care monthly max',
  );

  imputeBenefitAmount(
    monthlyNursingHomeBenefitAmount,
    firstNonZeroFacilityMonthlyBenefit,
    monthlyHomeBenefitAmount?.parsedFieldValue,
    'Imputed from other facility monthly max',
    'Imputed from Home Care monthly max',
  );

  return parsedResponse;
}

function calculateDailyMaxFromMonthlyMax(
  parsedResponse: ParsedPolicyDataExtractResponse,
): ParsedPolicyDataExtractResponse {
  const {
    dailyHomeBenefitAmount,
    dailyAssistedLivingBenefitAmount,
    dailyIndependentLivingBenefitAmount,
    dailyNursingHomeBenefitAmount,
    monthlyHomeBenefitAmount,
    monthlyAssistedLivingBenefitAmount,
    monthlyIndependentLivingBenefitAmount,
    monthlyNursingHomeBenefitAmount,
  } = parsedResponse;

  const calculateDailyMax = (
    targetField: (PolicyExtractedField & { parsedFieldValue: any }) | undefined,
    monthlyMaxField:
      | (PolicyExtractedField & { parsedFieldValue: any })
      | undefined,
    description: string,
  ) => {
    const daysInMonth = 30;
    if (
      isFieldAndParsedValueDefined(targetField) &&
      isFieldAndParsedValueDefined(monthlyMaxField) &&
      monthlyMaxField.parsedFieldValue > 0
    ) {
      const dailyMax = roundDownToNearestHundredth(
        monthlyMaxField.parsedFieldValue / daysInMonth,
      );
      targetField.fieldValue = dailyMax.toString();
      targetField.parsedFieldValue = dailyMax;
      targetField.locationDescription = description;
    }
  };

  calculateDailyMax(
    dailyHomeBenefitAmount,
    monthlyHomeBenefitAmount,
    'Calculated from Monthly Max Benefit for Home Care',
  );

  calculateDailyMax(
    dailyAssistedLivingBenefitAmount,
    monthlyAssistedLivingBenefitAmount,
    'Calculated from Monthly Max Benefit for Assisted Living Facility Care',
  );

  calculateDailyMax(
    dailyIndependentLivingBenefitAmount,
    monthlyIndependentLivingBenefitAmount,
    'Calculated from Monthly Max Benefit for Independent Living Facility Care',
  );

  calculateDailyMax(
    dailyNursingHomeBenefitAmount,
    monthlyNursingHomeBenefitAmount,
    'Calculated from Monthly Max Benefit for Nursing Home Facility Care',
  );

  return parsedResponse;
}

function imputeMissingDailyMaxValues(
  parsedResponse: ParsedPolicyDataExtractResponse,
): ParsedPolicyDataExtractResponse {
  const {
    dailyHomeBenefitAmount,
    dailyAssistedLivingBenefitAmount,
    dailyIndependentLivingBenefitAmount,
    dailyNursingHomeBenefitAmount,
  } = parsedResponse;

  const firstNonZeroFacilityDailyBenefit =
    dailyAssistedLivingBenefitAmount?.parsedFieldValue ||
    dailyIndependentLivingBenefitAmount?.parsedFieldValue ||
    dailyNursingHomeBenefitAmount?.parsedFieldValue;

  if (dailyHomeBenefitAmount && !dailyHomeBenefitAmount.parsedFieldValue) {
    if (firstNonZeroFacilityDailyBenefit) {
      dailyHomeBenefitAmount.fieldValue =
        firstNonZeroFacilityDailyBenefit.toString();
      dailyHomeBenefitAmount.parsedFieldValue =
        firstNonZeroFacilityDailyBenefit;
      dailyHomeBenefitAmount.locationDescription =
        'Imputed from first non-zero facility daily benefit';
    }
  }

  const imputeBenefitAmount = (
    targetField: (PolicyExtractedField & { parsedFieldValue: any }) | undefined,
    fillValue: number | undefined,
    fallbackValue: number | undefined,
    description: string,
    fallBackDescription: string,
  ) => {
    if (targetField && !targetField.parsedFieldValue) {
      if (fillValue) {
        targetField.fieldValue = fillValue.toString();
        targetField.parsedFieldValue = fillValue;
        targetField.locationDescription = description;
      } else if (fallbackValue) {
        targetField.fieldValue = fallbackValue.toString();
        targetField.parsedFieldValue = fallbackValue;
        targetField.locationDescription = fallBackDescription;
      }
    }
  };

  imputeBenefitAmount(
    dailyAssistedLivingBenefitAmount,
    firstNonZeroFacilityDailyBenefit,
    dailyHomeBenefitAmount?.parsedFieldValue,
    'Imputed from other facility daily max',
    'Imputed from Home Care daily max',
  );

  imputeBenefitAmount(
    dailyIndependentLivingBenefitAmount,
    firstNonZeroFacilityDailyBenefit,
    dailyHomeBenefitAmount?.parsedFieldValue,
    'Imputed from other facility daily max',
    'Imputed from Home Care daily max',
  );

  imputeBenefitAmount(
    dailyNursingHomeBenefitAmount,
    firstNonZeroFacilityDailyBenefit,
    dailyHomeBenefitAmount?.parsedFieldValue,
    'Imputed from other facility daily max',
    'Imputed from Home Care daily max',
  );

  return parsedResponse;
}

function calculateCOB(
  parsedResponse: ParsedPolicyDataExtractResponse,
  policyType: FundingPolicyType | null,
) {
  const {
    policyContinuationBenefitAmount,
    policyTotalLTCBenefitAmount,
    policyMaximumBenefitAmount,
  } = parsedResponse;

  // if COB is unlimited, do not calculate
  if (
    checkPolicyCalculationTypeEquals(
      policyType,
      FundingPolicyType.hybridLifeInsurance,
    ) &&
    policyContinuationBenefitAmount &&
    typeof policyContinuationBenefitAmount.fieldValue === 'string' &&
    policyContinuationBenefitAmount.fieldValue
      .toLowerCase()
      .includes('unlimited')
  ) {
    return parsedResponse;
  }

  const calculatedCOB =
    (policyTotalLTCBenefitAmount?.parsedFieldValue ?? 0) -
    (policyMaximumBenefitAmount?.parsedFieldValue ?? 0);

  // if COB is null and total LTC benefit amount is imported, calculate COB
  if (
    checkPolicyCalculationTypeEquals(
      policyType,
      FundingPolicyType.hybridLifeInsurance,
    ) &&
    isFieldAndParsedValueDefined(policyContinuationBenefitAmount) &&
    isFieldAndParsedValueDefined(policyTotalLTCBenefitAmount)
  ) {
    policyContinuationBenefitAmount.fieldValue = calculatedCOB.toString();
    policyContinuationBenefitAmount.parsedFieldValue = calculatedCOB;
    policyContinuationBenefitAmount.locationDescription =
      'Calculated from Total Long-Term Care Benefits Amount and Face Amount (Death Benefit)';
  }

  // if COB and total LTC benefit amount are imported, check if calculated COB is different from imported COB
  if (
    checkPolicyCalculationTypeEquals(
      policyType,
      FundingPolicyType.hybridLifeInsurance,
    ) &&
    isFieldAndParsedValueDefined(policyContinuationBenefitAmount) &&
    isFieldAndParsedValueDefined(policyTotalLTCBenefitAmount) &&
    policyContinuationBenefitAmount.parsedFieldValue !== calculatedCOB
  ) {
    policyContinuationBenefitAmount.fieldValue = calculatedCOB.toString();
    policyContinuationBenefitAmount.parsedFieldValue = calculatedCOB;
    policyContinuationBenefitAmount.locationDescription =
      'Calculated from Total Long-Term Care Benefits Amount and Face Amount (Death Benefit)';
  }
  return parsedResponse;
}

function checkLimitedPayPeriodAgainstPremiums(
  parsedResponse: ParsedPolicyDataExtractResponse,
): ParsedPolicyDataExtractResponse {
  const {
    policyLimitedPayYears,
    policyPremiumMonthlyCost,
    policyLumpSumPayment,
  } = parsedResponse;

  // if lump sum payment is greater than 0 and monthly premium is 0 or undefined, limited pay period should be null
  if (
    isFieldAndParsedValueDefined(policyLumpSumPayment) &&
    policyLumpSumPayment.parsedFieldValue > 0 &&
    policyLimitedPayYears &&
    (policyPremiumMonthlyCost === undefined ||
      !policyPremiumMonthlyCost?.parsedFieldValue)
  ) {
    policyLimitedPayYears.fieldValue = null;
    policyLimitedPayYears.parsedFieldValue = undefined;
    policyLimitedPayYears.locationDescription = null;
  }

  return parsedResponse;
}

function hybridIraPostProcessing(
  parsedResponse: ParsedPolicyDataExtractResponse,
  policyType: FundingPolicyType | null,
): ParsedPolicyDataExtractResponse {
  if (
    !checkPolicyCalculationTypeEquals(
      policyType,
      FundingPolicyType.hybridLifeIra,
    )
  ) {
    return parsedResponse;
  }

  // and set COB to 0
  const { policyHasCobRider } = parsedResponse;

  // if policy does not have COB rider,
  if (policyHasCobRider?.parsedFieldValue === 'No') {
    //nullify all benefit amounts
    const fieldsToNullify = [
      'monthlyHomeBenefitAmount',
      'monthlyAssistedLivingBenefitAmount',
      'monthlyIndependentLivingBenefitAmount',
      'monthlyNursingHomeBenefitAmount',
      'dailyHomeBenefitAmount',
      'dailyAssistedLivingBenefitAmount',
      'dailyIndependentLivingBenefitAmount',
      'dailyNursingHomeBenefitAmount',
    ] as const;
    fieldsToNullify.forEach(field => {
      const parsedField = parsedResponse[field];
      if (parsedField) {
        parsedField.fieldValue = null;
        parsedField.parsedFieldValue = null;
      }
    });

    // set COB to 0
    const zeroCobParsedField = {
      fieldTitle: 'Continuation of Benefits',
      fieldValue: '0',
      parsedFieldValue: 0,
      pageNumber: null,
      locationDescription: 'Imputed from policy not having COB rider',
    };
    parsedResponse['policyContinuationBenefitAmount'] = zeroCobParsedField;
  }

  return parsedResponse;
}

function nylSecureCarePostProcessing(
  parsedResponse: ParsedPolicyDataExtractResponse,
  policyType: FundingPolicyType | null,
) {
  if (policyType !== FundingPolicyType.nylSecureCare) {
    return parsedResponse;
  }

  const { policyInflationProtection, policyPremiumIncreaseRate } =
    parsedResponse;

  const inflationProtectionPercentage =
    policyInflationProtection?.parsedFieldValue;

  // if inflation protection is not defined, set it to 0
  if (isNullish(inflationProtectionPercentage)) {
    const locationDescription =
      'Imputed from no inflation protection mentioned in the policy';
    if (!policyInflationProtection) {
      parsedResponse.policyInflationProtection = {
        fieldTitle: 'Inflation Protection',
        fieldValue: '0',
        parsedFieldValue: 0,
        pageNumber: null,
        locationDescription,
      };
    } else {
      policyInflationProtection.fieldValue = '0';
      policyInflationProtection.parsedFieldValue = 0;
      policyInflationProtection.locationDescription = locationDescription;
    }
  }

  // if premium increase rate is not defined
  if (isNullish(policyPremiumIncreaseRate?.parsedFieldValue)) {
    const locationDescription =
      'Imputed from no premium increase rate mentioned in the policy';
    const policyPremiumIncreaseRate = inflationProtectionPercentage
      ? inflationProtectionPercentage
      : 0;

    if (!policyPremiumIncreaseRate) {
      parsedResponse.policyPremiumIncreaseRate = {
        fieldTitle: 'Premium Increase Rate',
        fieldValue: policyPremiumIncreaseRate.toString(),
        parsedFieldValue: policyPremiumIncreaseRate,
        pageNumber: null,
        locationDescription,
      };
    } else {
      policyPremiumIncreaseRate.fieldValue = '0';
      policyPremiumIncreaseRate.parsedFieldValue = 0;
      policyPremiumIncreaseRate.locationDescription = locationDescription;
    }
  }

  return parsedResponse;
}

export const postProcessParsedResponse = (
  parsedResponse: ParsedPolicyDataExtractResponse,
  policyType: FundingPolicyType | null,
): ParsedPolicyDataExtractResponse => {
  // Impute missing monthly max values if necessary
  parsedResponse = imputeMissingMonthlyMaxValues(parsedResponse);

  // Calculate daily max from monthly max if necessary
  parsedResponse = calculateDailyMaxFromMonthlyMax(parsedResponse);

  // Impute missing daily max values if necessary
  parsedResponse = imputeMissingDailyMaxValues(parsedResponse);

  // Calculate COB if necessary
  parsedResponse = calculateCOB(parsedResponse, policyType);

  // Check limited pay period against premiums
  parsedResponse = checkLimitedPayPeriodAgainstPremiums(parsedResponse);

  parsedResponse = hybridIraPostProcessing(parsedResponse, policyType);

  parsedResponse = nylSecureCarePostProcessing(parsedResponse, policyType);

  return parsedResponse;
};
